import { createAsyncThunk } from '@reduxjs/toolkit';
import { StoreState } from "../../store/store.interface";
import { getGuestEstimatedTaxMagentoApiCall, getLoggedInEstimatedTaxMagentoApiCall } from './index';
import { EstimatedTax, GetEstimatedTaxParams } from './estimated-tax.interface';
import { getCartContext, getGuestCartToken } from '../cart/shop-cart/store/shop-cart.selectors';
import { isGuestCheckoutPage } from '../../common/environment';
import { ActionStatus } from '../../common/constants/action-status';
import { ShopCartUrlContext } from '../../common/utils/cart-util';
import { getCustomerToken, isCustomerLoginSuccess } from '../customer/customer';
import { getCartIdFromURL } from '../../common/constants/guest-checkout-api';

/*----- DECLARE THUNKS ------*/

export const getEstimatedTax = createAsyncThunk('shopCart/getEstimatedTax', async ({ zipCode, promoCode, storeCode }: GetEstimatedTaxParams, thunkAPI) => {
  const state = thunkAPI.getState() as StoreState;
  const cartContext = getCartContext(state, storeCode);

  if (typeof promoCode !== 'undefined') {
    // If an empty string is passed in as the promocode, make sure to override the promocode that is in redux
    // This is used by the promoCode when a user enters an invalid promo code
    cartContext.promoCode = promoCode;
  }

  if (zipCode) {
    cartContext.zipCode = zipCode;
  }

  try {
    if (cartContext.storeCode && cartContext.zipCode) {
      const extimatedTax: EstimatedTax = await getMagentoEstimatedTax(state, cartContext);
      return {
        ...extimatedTax,
        zipCode: cartContext.zipCode
      };
    } else {
      const messages = ['Missing information in getEstimatedTax'];
      cartContext.storeCode || messages.push('Missing storeCode');
      cartContext.zipCode || messages.push('Missing zipCode');
      return thunkAPI.rejectWithValue({
        message: 'Missing information in getEstimatedTax',
        zipCode: cartContext.zipCode
      });
    }
  } catch (error) {
    return thunkAPI.rejectWithValue({
      error: error,
      zipCode: cartContext.zipCode
    });
  }

}, {
  condition: ((_args: GetEstimatedTaxParams, { getState }) => {
    const state = getState() as StoreState;
    return ![ActionStatus.loading].includes(state.estimatedTaxState.status);
  })
});

async function getMagentoEstimatedTax(state: StoreState, cartContext: ShopCartUrlContext):Promise<EstimatedTax> {
  if (isCustomerLoginSuccess(state)) {
    const customerToken = getCustomerToken(state);
    if (customerToken) {
      return await getLoggedInEstimatedTaxMagentoApiCall(customerToken, cartContext);
    } else {
      throw new Error('customerToken not defined');
    }
  } else if (isGuestCheckoutPage()) {
    return await getGuestEstimatedTaxMagentoApiCall(getCartIdFromURL(), cartContext);
  } else {
    const guestCartToken = getGuestCartToken(state);
    if (guestCartToken) {
      return await getGuestEstimatedTaxMagentoApiCall(guestCartToken, cartContext);
    } else {
      throw new Error('magentoToken not defined');
    }
  }
}
